import { useEffect, useRef } from "react";
import * as THREE from "three";

export function StarfieldCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const dprWidth = width * pixelRatio;
    const dprHeight = height * pixelRatio;
    const resScale = Math.max(1.0, height / 1080);

    // 1. Setup Scene, Camera & Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 12;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(pixelRatio);

    // Post-processing setup
    const renderTarget = new THREE.WebGLRenderTarget(dprWidth, dprHeight, {
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });

    // Ping-pong targets for temporal accumulation motion blur
    let accumTarget1 = new THREE.WebGLRenderTarget(dprWidth, dprHeight, {
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });

    let accumTarget2 = new THREE.WebGLRenderTarget(dprWidth, dprHeight, {
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });

    const postScene = new THREE.Scene();
    const postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Generate ASCII character atlas texture offscreen
    const createAsciiTexture = (scale: number) => {
      const chars = " .:-=+*#%@";
      const charWidth = 8 * scale;
      const charHeight = 14 * scale;
      const canvas = document.createElement("canvas");
      canvas.width = charWidth * chars.length;
      canvas.height = charHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `bold ${Math.round(12 * scale)}px monospace`;
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      for (let i = 0; i < chars.length; i++) {
        ctx.fillText(chars[i], i * charWidth + charWidth / 2, charHeight / 2);
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      return texture;
    };

    const asciiTexture = createAsciiTexture(pixelRatio);

    // 1. Motion Blur shader (decays and accumulates frames)
    const blurScene = new THREE.Scene();
    const blurMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tCurrent: { value: null },
        tPrevious: { value: null },
        uDecay: { value: 0.993 }, // Extremely high decay factor for very long trails!
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tCurrent;
        uniform sampler2D tPrevious;
        uniform float uDecay;
        varying vec2 vUv;
        void main() {
          vec4 current = texture2D(tCurrent, vUv);
          vec4 previous = texture2D(tPrevious, vUv);
          // Blend current frame over decaying previous frame
          gl_FragColor = max(current, previous * uDecay);
        }
      `,
      transparent: true,
    });

    const blurGeometry = new THREE.PlaneGeometry(2, 2);
    const blurQuad = new THREE.Mesh(blurGeometry, blurMaterial);
    blurScene.add(blurQuad);

    // 2. Chromatic Aberration & ASCII shader (distorts and maps to ASCII text cells)
    const postMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tDiffuse: { value: null },
        uAmount: { value: 0.28 },
        uResolution: { value: new THREE.Vector2(dprWidth, dprHeight) },
        uCharAtlas: { value: asciiTexture },
        uCharSize: { value: new THREE.Vector2(8 * pixelRatio, 14 * pixelRatio) },
        uCharCount: { value: 10.0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float uAmount;
        uniform vec2 uResolution;
        uniform sampler2D uCharAtlas;
        uniform vec2 uCharSize;
        uniform float uCharCount;
        varying vec2 vUv;

        void main() {
          // 1. Calculate ASCII coordinates
          vec2 pixelCoord = vUv * uResolution;
          vec2 cellCoord = floor(pixelCoord / uCharSize) * uCharSize;
          vec2 cellCenterUv = (cellCoord + uCharSize * 0.5) / uResolution;

          // 2. Sample chromatic aberration at the center of the cell
          vec2 cellDir = cellCenterUv - vec2(0.5);
          float cellDist = length(cellDir);
          float cellFactor = cellDist * cellDist * cellDist * cellDist * uAmount;
          vec2 cellOffset = cellDir * cellFactor;

          vec4 rCol = texture2D(tDiffuse, cellCenterUv - cellOffset);
          vec4 gCol = texture2D(tDiffuse, cellCenterUv);
          vec4 bCol = texture2D(tDiffuse, cellCenterUv + cellOffset);
          vec4 cellColor = vec4(rCol.r, gCol.g, bCol.b, max(max(rCol.a, gCol.a), bCol.a));

          // 3. Calculate luminance and select character index
          float luma = dot(cellColor.rgb, vec3(0.299, 0.587, 0.114));
          float charIdx = floor(luma * (uCharCount - 0.001));

          // 4. Sample the character atlas
          vec2 localUv = mod(pixelCoord, uCharSize) / uCharSize;
          vec2 atlasUv = vec2((charIdx + localUv.x) / uCharCount, localUv.y);
          vec4 charVal = texture2D(uCharAtlas, atlasUv);

          // Apply color and transparency based on character pixel brightness
          gl_FragColor = vec4(cellColor.rgb * charVal.r, cellColor.a * charVal.r);
        }
      `,
      transparent: true,
    });

    const postGeometry = new THREE.PlaneGeometry(2, 2);
    const postQuad = new THREE.Mesh(postGeometry, postMaterial);
    postScene.add(postQuad);

    // 2. Generate Custom Glowing Glow Particle Texture (with 4-point lens flare sparkle)
    const createParticleTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      // Draw central soft radial glow (sharp white core, compact bright color glow)
      const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 16);
      grad.addColorStop(0, "rgba(255, 255, 255, 1.0)");
      grad.addColorStop(0.12, "rgba(255, 255, 255, 1.0)");
      grad.addColorStop(0.28, "rgba(238, 255, 255, 0.85)");
      grad.addColorStop(0.55, "rgba(6, 182, 212, 0.65)");
      grad.addColorStop(0.8, "rgba(59, 130, 246, 0.3)");
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 64, 64);

      // Draw horizontal lens flare beam
      const gradH = ctx.createLinearGradient(16, 32, 48, 32);
      gradH.addColorStop(0, "rgba(6, 182, 212, 0)");
      gradH.addColorStop(0.38, "rgba(6, 182, 212, 0.65)");
      gradH.addColorStop(0.48, "rgba(255, 255, 255, 0.98)");
      gradH.addColorStop(0.52, "rgba(255, 255, 255, 0.98)");
      gradH.addColorStop(0.62, "rgba(6, 182, 212, 0.65)");
      gradH.addColorStop(1, "rgba(6, 182, 212, 0)");
      ctx.fillStyle = gradH;
      ctx.fillRect(16, 31.0, 32, 2.0);

      // Draw vertical lens flare beam
      const gradV = ctx.createLinearGradient(32, 16, 32, 48);
      gradV.addColorStop(0, "rgba(6, 182, 212, 0)");
      gradV.addColorStop(0.38, "rgba(6, 182, 212, 0.65)");
      gradV.addColorStop(0.48, "rgba(255, 255, 255, 0.98)");
      gradV.addColorStop(0.52, "rgba(255, 255, 255, 0.98)");
      gradV.addColorStop(0.62, "rgba(6, 182, 212, 0.65)");
      gradV.addColorStop(1, "rgba(6, 182, 212, 0)");
      ctx.fillStyle = gradV;
      ctx.fillRect(31.0, 16, 2.0, 32);

      return new THREE.CanvasTexture(canvas);
    };

    const particleTexture = createParticleTexture();

    // 3. Particle Settings & Memory Allocation (scaled with screen resolution)
    const area = width * height;
    const baselineArea = 5000000; // Reference area for 2K/4K ultrawide viewports
    // Reduced particle count for clean, spaced-out ASCII layout
    const calculatedCount = Math.round(1500 * (area / baselineArea));
    const particleCount = Math.max(360, Math.min(calculatedCount, 1650));
    const geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(particleCount * 3);
    const basePositions = new Float32Array(particleCount * 3);
    const baseVelocities = new Float32Array(particleCount * 3);
    const displacements = new Float32Array(particleCount * 3);
    const dispVelocities = new Float32Array(particleCount * 3);
    const baseColors = new Float32Array(particleCount * 3);
    const currentColors = new Float32Array(particleCount * 3);
    const flickerOffsets = new Float32Array(particleCount);

    // Boundary limits in ThreeJS space for particle wrapping
    const bounds = { x: 18, y: 12, z: 10 };

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      const idx = i * 3;

      // Random initial position inside bounds
      const x = (Math.random() - 0.5) * bounds.x * 2;
      const y = (Math.random() - 0.5) * bounds.y * 2;
      const z = (Math.random() - 0.5) * bounds.z * 2;

      positions[idx] = x;
      positions[idx + 1] = y;
      positions[idx + 2] = z;

      basePositions[idx] = x;
      basePositions[idx + 1] = y;
      basePositions[idx + 2] = z;

      // Very slow even drift velocity
      baseVelocities[idx] = (Math.random() - 0.5) * 0.35;
      baseVelocities[idx + 1] = (Math.random() - 0.5) * 0.35;
      baseVelocities[idx + 2] = (Math.random() - 0.5) * 0.25;

      displacements[idx] = 0;
      displacements[idx + 1] = 0;
      displacements[idx + 2] = 0;

      dispVelocities[idx] = 0;
      dispVelocities[idx + 1] = 0;
      dispVelocities[idx + 2] = 0;

      // Assign pure white base colors
      baseColors[idx] = 1.0;
      baseColors[idx + 1] = 1.0;
      baseColors[idx + 2] = 1.0;

      currentColors[idx] = baseColors[idx];
      currentColors[idx + 1] = baseColors[idx + 1];
      currentColors[idx + 2] = baseColors[idx + 2];

      flickerOffsets[i] = Math.random() * Math.PI * 2;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(currentColors, 3));

    // 4. Points Material (fine sparkle details, calibrated opacity)
    const material = new THREE.PointsMaterial({
      size: 1.5 * resScale, // Larger size for ASCII characters, scaled with resolution
      vertexColors: true,
      map: particleTexture || undefined,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.96,
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // 4.5. Mouse-attached glowing particle (separate Points object for custom size/brightness)
    const mouseGeometry = new THREE.BufferGeometry();
    const mousePositions = new Float32Array(3); // 1 point
    mouseGeometry.setAttribute("position", new THREE.BufferAttribute(mousePositions, 3));

    const mouseMaterial = new THREE.PointsMaterial({
      size: 4.5 * resScale, // Brighter and bigger (originally 1.5, now 4.5), scaled with resolution
      color: 0xffffff, // 100% white base color (which maps to full brightness)
      map: particleTexture || undefined,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 1.0, // Maximum opacity/brightness
    });

    const mouseParticle = new THREE.Points(mouseGeometry, mouseMaterial);
    scene.add(mouseParticle);

    let currentPixelRatio = pixelRatio;

    // 5. Event Listeners (Resize & Mouse Tracking)
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const pr = Math.min(window.devicePixelRatio, 2);
      const dprW = w * pr;
      const dprH = h * pr;
      const newResScale = Math.max(1.0, h / 1080);

      camera.aspect = w / h;
      camera.updateProjectionMatrix();

      renderer.setSize(w, h);
      renderer.setPixelRatio(pr);

      renderTarget.setSize(dprW, dprH);
      accumTarget1.setSize(dprW, dprH);
      accumTarget2.setSize(dprW, dprH);

      postMaterial.uniforms.uResolution.value.set(dprW, dprH);
      postMaterial.uniforms.uCharSize.value.set(8 * pr, 14 * pr);

      material.size = 1.5 * newResScale;
      mouseMaterial.size = 4.5 * newResScale;

      if (pr !== currentPixelRatio) {
        currentPixelRatio = pr;
        if (postMaterial.uniforms.uCharAtlas.value) {
          postMaterial.uniforms.uCharAtlas.value.dispose();
        }
        const newTexture = createAsciiTexture(pr);
        postMaterial.uniforms.uCharAtlas.value = newTexture;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.targetX = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    // 6. Animation Loop
    let lastTime = performance.now();
    const startTime = performance.now();
    let animId: number;

    const render = () => {
      animId = requestAnimationFrame(render);

      const currentTime = performance.now();
      const delta = Math.min((currentTime - lastTime) / 1000, 0.1);
      lastTime = currentTime;

      const time = (currentTime - startTime) / 1000;

      // Track vertical scroll position in world units
      const scrollWorldY = window.scrollY * 0.007;

      // Smooth tracking of mouse position
      mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.05;
      mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.05;

      const posAttr = geometry.attributes.position as THREE.BufferAttribute;
      const posArray = posAttr.array as Float32Array;

      const colorAttr = geometry.attributes.color as THREE.BufferAttribute;
      const colorArray = colorAttr.array as Float32Array;

      // Project mouse screen coordinates to world space at z = 0 plane
      const mouseVec = new THREE.Vector3(mouseRef.current.x, mouseRef.current.y, 0.5);
      mouseVec.unproject(camera);
      const dir = mouseVec.sub(camera.position).normalize();
      const distance = -camera.position.z / dir.z;
      const mouseWorld = camera.position.clone().add(dir.multiplyScalar(distance));

      const mouseWorldX = mouseWorld.x;
      const mouseWorldY = mouseWorld.y;

      for (let i = 0; i < particleCount; i++) {
        const idx = i * 3;

        // 1. Update undisturbed base position with drift
        basePositions[idx] += baseVelocities[idx] * delta;
        basePositions[idx + 1] += baseVelocities[idx + 1] * delta;
        basePositions[idx + 2] += baseVelocities[idx + 2] * delta;

        // 2. Wrap-around boundaries for base positions
        const margin = 1.0;
        if (basePositions[idx] > bounds.x + margin) basePositions[idx] = -bounds.x;
        else if (basePositions[idx] < -bounds.x - margin) basePositions[idx] = bounds.x;

        if (basePositions[idx + 1] > bounds.y + margin) basePositions[idx + 1] = -bounds.y;
        else if (basePositions[idx + 1] < -bounds.y - margin) basePositions[idx + 1] = bounds.y;

        if (basePositions[idx + 2] > bounds.z + margin) basePositions[idx + 2] = -bounds.z;
        else if (basePositions[idx + 2] < -bounds.z - margin) basePositions[idx + 2] = bounds.z;

        const bx = basePositions[idx];
        const by = basePositions[idx + 1];
        const bz = basePositions[idx + 2];

        // 3. Elastic displacement physics
        const springX = -displacements[idx] * 12.0;
        const springY = -displacements[idx + 1] * 12.0;

        const dragX = -dispVelocities[idx] * 2.8;
        const dragY = -dispVelocities[idx + 1] * 2.8;

        // Calculate Y coordinate with scroll offset added
        const pyRaw = by + displacements[idx + 1] + scrollWorldY;
        const yRange = bounds.y * 2;
        const py = ((((pyRaw + bounds.y) % yRange) + yRange) % yRange) - bounds.y;

        const rx = bx + displacements[idx];
        const ry = py;

        const dxMouse = rx - mouseWorldX;
        const dyMouse = ry - mouseWorldY;
        const distMouseSq = dxMouse * dxMouse + dyMouse * dyMouse;
        const distMouse = Math.sqrt(distMouseSq);

        let repX = 0;
        let repY = 0;
        const pushRadius = 2.6;
        if (distMouse < pushRadius && distMouse > 0.05) {
          const force = (1.0 - distMouse / pushRadius) * 7.5;
          repX = -(dxMouse / distMouse) * force;
          repY = -(dyMouse / distMouse) * force;
        }

        const ax = springX + dragX + repX;
        const ay = springY + dragY + repY;

        dispVelocities[idx] += ax * delta;
        dispVelocities[idx + 1] += ay * delta;

        displacements[idx] += dispVelocities[idx] * delta;
        displacements[idx + 1] += dispVelocities[idx + 1] * delta;

        posArray[idx] = bx + displacements[idx];
        posArray[idx + 1] = py;
        posArray[idx + 2] = bz;

        // 5. Vignette fade-out near edges
        const dX = Math.abs(posArray[idx]) / bounds.x;
        const dY = Math.abs(posArray[idx + 1]) / bounds.y;
        const vigX = Math.max(0, 1.0 - dX * dX * dX);
        const vigY = Math.max(0, 1.0 - dY * dY * dY);
        const vignette = vigX * vigY;

        // 6. Twinkling color brightness flicker (slower, gentle twinkles)
        const phase = flickerOffsets[i];
        const flicker = Math.sin(time * (0.25 + phase * 0.1) + phase) * 0.42 + 0.58;

        const finalColor = flicker * vignette;
        colorArray[idx] = baseColors[idx] * finalColor;
        colorArray[idx + 1] = baseColors[idx + 1] * finalColor;
        colorArray[idx + 2] = baseColors[idx + 2] * finalColor;
      }

      // Update mouse particle position
      const mousePosAttr = mouseGeometry.attributes.position as THREE.BufferAttribute;
      const mousePosArray = mousePosAttr.array as Float32Array;
      mousePosArray[0] = mouseWorldX;
      mousePosArray[1] = mouseWorldY;
      mousePosArray[2] = 0; // keeps particle on z=0 plane
      mousePosAttr.needsUpdate = true;

      posAttr.needsUpdate = true;
      colorAttr.needsUpdate = true;

      // Slow ambient camera rotation
      camera.position.x = Math.sin(time * 0.1) * 0.4;
      camera.position.y = Math.cos(time * 0.15) * 0.3;

      // 1. Render main particle scene to renderTarget
      renderer.setRenderTarget(renderTarget);
      renderer.clear();
      renderer.render(scene, camera);

      // 2. Render motion blur accumulation to accumTarget2 (blending renderTarget and accumTarget1)
      renderer.setRenderTarget(accumTarget2);
      renderer.clear();
      blurMaterial.uniforms.tCurrent.value = renderTarget.texture;
      blurMaterial.uniforms.tPrevious.value = accumTarget1.texture;
      renderer.render(blurScene, postCamera);

      // 3. Render chromatic aberration + ASCII pass directly to screen (using accumTarget2 as input)
      renderer.setRenderTarget(null);
      postMaterial.uniforms.tDiffuse.value = accumTarget2.texture;
      renderer.render(postScene, postCamera);

      // 4. Ping-Pong swap the accumulation targets
      const temp = accumTarget1;
      accumTarget1 = accumTarget2;
      accumTarget2 = temp;
    };

    render();

    // 7. Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animId);

      scene.remove(particles);
      geometry.dispose();
      material.dispose();
      if (particleTexture) particleTexture.dispose();

      scene.remove(mouseParticle);
      mouseGeometry.dispose();
      mouseMaterial.dispose();

      postScene.remove(postQuad);
      postGeometry.dispose();
      postMaterial.dispose();
      renderTarget.dispose();
      if (postMaterial.uniforms.uCharAtlas.value) {
        postMaterial.uniforms.uCharAtlas.value.dispose();
      }

      blurScene.remove(blurQuad);
      blurGeometry.dispose();
      blurMaterial.dispose();
      accumTarget1.dispose();
      accumTarget2.dispose();

      renderer.dispose();
    };
  }, []);

  return (
    <div className="fixed inset-0 w-full h-full z-0 pointer-events-none overflow-hidden bg-gray-950">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
}
